<template>
  <div class="container-fluid">
    <!-- Bread-crumb -->
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>

    <!-- 403: inline panel rather than a toast, so the operator knows to ask their admin -->
    <div v-if="forbidden" class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <div class="body text-center py-5">
            <i class="fa fa-lock fa-2x text-muted mb-3"></i>
            <h5>You don't have permission to view holds</h5>
            <p class="text-muted mb-0">Contact your administrator to request the <code>hold.view</code> permission.</p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <div class="header d-flex justify-content-between align-items-center">
            <h2>Hold List</h2>
          </div>

          <div class="body pt-0">
            <!-- Filter row: [search] [status] [page size] [Refresh] -->
            <div class="d-flex flex-wrap align-items-end justify-content-between mb-3">
              <div class="d-flex flex-wrap align-items-end">
                <div class="mr-3 mb-2" style="min-width: 260px;">
                  <label class="text-muted small mb-1">Search</label>
                  <b-form-input
                    v-model="search"
                    placeholder="Hold ID, Wallet ID, or Currency"
                    @input="onSearchInput"
                  />
                </div>
                <div class="mr-3 mb-2">
                  <label class="text-muted small mb-1">Status</label>
                  <b-form-select
                    v-model="statusFilter"
                    :options="statusOptions"
                    @change="onFilterChange"
                  />
                </div>
                <div class="mb-2">
                  <label class="text-muted small mb-1">Per page</label>
                  <b-form-select
                    v-model="pageSize"
                    :options="pageSizeOptions"
                    @change="onFilterChange"
                  />
                </div>
              </div>
              <div class="mb-2">
                <b-button variant="outline-primary" :disabled="loading" @click="refresh">
                  <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> Refresh
                </b-button>
              </div>
            </div>

            <!-- List-fetch error banner: keeps the last-good rows visible underneath -->
            <b-alert :show="!!errorBanner" variant="danger" dismissible @dismissed="errorBanner = ''">
              {{ errorBanner }}
              <b-button size="sm" variant="danger" class="ml-2" @click="refresh">Retry</b-button>
            </b-alert>

            <!-- Table -->
            <b-table
              responsive
              head-variant="light"
              :items="items"
              :fields="fields"
              :busy="loading"
              show-empty
              class="mb-0"
            >
              <template #table-busy>
                <div class="text-center my-3">
                  <b-spinner small></b-spinner> Loading holds…
                </div>
              </template>

              <template #empty>
                <div class="text-center text-muted my-4">
                  <template v-if="hasActiveFilters">
                    No holds match your filters.
                    <b-button size="sm" variant="link" @click="clearFilters">Clear filters</b-button>
                  </template>
                  <template v-else>No holds found.</template>
                </div>
              </template>

              <template #cell(hold_id)="row">
                <span v-b-tooltip.hover :title="row.item.hold_id">{{ truncateId(row.item.hold_id) }}</span>
                <i class="fa fa-copy ml-1 cursor-pointer text-muted" @click="copy(row.item.hold_id, 'Hold ID')"></i>
              </template>

              <template #cell(wallet_id)="row">
                <span v-b-tooltip.hover :title="row.item.wallet_id">{{ truncateId(row.item.wallet_id) }}</span>
                <i class="fa fa-copy ml-1 cursor-pointer text-muted" @click="copy(row.item.wallet_id, 'Wallet ID')"></i>
              </template>

              <template #cell(amount)="row">
                {{ formatAmount(row.item.amount) }}
              </template>

              <template #cell(status)="row">
                <span class="badge" :class="statusBadgeClass(row.item.status)">{{ row.item.status }}</span>
              </template>

              <template #cell(reason)="row">
                <span>{{ reasonPrimary(row.item) }}</span>
                <i
                  v-if="reasonHasDetail(row.item)"
                  class="fa fa-info-circle ml-1 text-muted cursor-pointer"
                  v-b-tooltip.hover.html
                  :title="reasonTooltip(row.item)"
                ></i>
              </template>

              <template #cell(created_at)="row">
                {{ formatUtcDateTime(row.item.created_at) }}
              </template>

              <template #cell(expires_at)="row">
                <span :class="{ 'text-danger font-weight-bold': isExpiredRow(row.item) }">
                  {{ formatUtcDateTime(row.item.expires_at) }}
                  <span v-if="isExpiredRow(row.item)"> (expired)</span>
                </span>
              </template>

              <template #cell(actions)="row">
                <!-- Terminal states: read-only label -->
                <span v-if="rowAction(row.item).kind === 'terminal_label'" class="text-muted">
                  {{ rowAction(row.item).label }}
                  <span v-if="rowAction(row.item).at"> {{ formatUtcDateTime(rowAction(row.item).at) }}</span>
                </span>

                <!-- Actionable PENDING rows: dropdown of permitted actions -->
                <b-dropdown
                  v-else-if="rowAction(row.item).kind.indexOf('dropdown') === 0"
                  size="sm"
                  variant="outline-primary"
                  text="Action"
                >
                  <b-dropdown-item
                    v-if="rowAction(row.item).actions.includes('confirm')"
                    @click="openConfirm(row.item)"
                  >Confirm</b-dropdown-item>
                  <b-dropdown-item
                    v-if="rowAction(row.item).actions.includes('reverse')"
                    @click="openReverse(row.item)"
                  >Reverse</b-dropdown-item>
                </b-dropdown>

                <span v-else class="text-muted">—</span>
              </template>
            </b-table>

            <!-- Cursor pagination footer -->
            <div class="d-flex justify-content-end align-items-center mt-3">
              <b-button-group size="sm">
                <b-button variant="outline-secondary" :disabled="!pager.canPrev() || loading" @click="prevPage">
                  ‹ Prev
                </b-button>
                <b-button variant="outline-secondary" :disabled="!nextCursor || loading" @click="nextPage">
                  Next ›
                </b-button>
              </b-button-group>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <hold-confirm-modal :show="confirmModal.show" :hold="confirmModal.hold" @confirm="doConfirm" @cancel="confirmModal.show = false" />
    <hold-reverse-modal :show="reverseModal.show" :hold="reverseModal.hold" @reverse="doReverse" @cancel="reverseModal.show = false" />
    <hold-result-modal
      :show="resultModal.show"
      :action="resultModal.action"
      :success="resultModal.success"
      :result="resultModal.result"
      :error="resultModal.error"
      :hold="resultModal.hold"
      :loading="resultModal.loading"
      @retry="retryAction"
      @close="closeResult"
    />
  </div>
</template>

<script>
import BreadCrumb from "@/components/BreadCrumb.vue";
import HoldConfirmModal from "@/components/admin/holds/HoldConfirmModal.vue";
import HoldReverseModal from "@/components/admin/holds/HoldReverseModal.vue";
import HoldResultModal from "@/components/admin/holds/HoldResultModal.vue";
import { createCursorPager } from "@/utils/holds/cursorPagination";
import { formatReason } from "@/utils/holds/formatReason";
import { formatAmount, formatUtcDateTime, truncateId } from "@/utils/holds/formatters";
import { getRowAction, isPastExpiry } from "@/utils/holds/rowActionPolicy";

const STATUS_BADGES = {
  PENDING: "badge-warning",
  CONFIRMED: "badge-success",
  REVERSED: "badge-secondary",
  EXPIRED: "badge-danger",
};

export default {
  name: "HoldsListComponent",
  components: {
    BreadCrumb,
    HoldConfirmModal,
    HoldReverseModal,
    HoldResultModal,
  },
  data() {
    return {
      items: [],
      loading: false,
      errorBanner: "",
      forbidden: false,

      search: "",
      statusFilter: "PENDING",
      pageSize: 10,

      // Stale-response guard: only the latest token's response is committed.
      requestToken: 0,
      searchTimer: null,

      pager: createCursorPager(),
      nextCursor: null,
      now: Date.now(),

      statusOptions: [
        { value: "PENDING", text: "Pending" },
        { value: "CONFIRMED", text: "Confirmed" },
        { value: "REVERSED", text: "Reversed" },
        { value: "EXPIRED", text: "Expired" },
        { value: "ALL", text: "All" },
      ],
      pageSizeOptions: [10, 25, 50, 100],

      fields: [
        { key: "hold_id", label: "Hold ID" },
        { key: "wallet_id", label: "Wallet ID" },
        { key: "amount", label: "Amount", thClass: "text-right", tdClass: "text-right" },
        { key: "currency_code", label: "Currency" },
        { key: "status", label: "Status" },
        { key: "reason", label: "Reason" },
        { key: "created_at", label: "Created At" },
        { key: "expires_at", label: "Expires At" },
        { key: "actions", label: "Action" },
      ],

      confirmModal: { show: false, hold: null },
      reverseModal: { show: false, hold: null },
      resultModal: {
        show: false,
        action: "confirm",
        success: false,
        result: {},
        error: null,
        hold: null,
        loading: false,
        idempotencyKey: null,
      },
    };
  },
  computed: {
    hasActiveFilters() {
      return this.search.trim() !== "" || this.statusFilter !== "PENDING";
    },
  },
  created() {
    this.fetchHolds({ reset: true });
  },
  beforeDestroy() {
    // Cancel a pending debounced search so fetchHolds can't fire after teardown.
    // The requestToken guard already drops the response, but clearing the timer
    // avoids the wasted dispatch and any post-destroy state mutation warnings.
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    formatAmount,
    formatUtcDateTime,
    truncateId,

    hasPermission(p) {
      return this.$store.getters["authpermission/hasPermission"](p);
    },

    // ── Query + fetch ────────────────────────────────────────────────────
    defaultSort() {
      // PENDING surfaces soonest-to-expire first; everything else is newest-first.
      return this.statusFilter === "PENDING" ? "expires_at_asc" : "created_at_desc";
    },
    buildQuery() {
      const params = new URLSearchParams();
      if (this.statusFilter && this.statusFilter !== "ALL") params.append("status", this.statusFilter);
      if (this.search.trim()) params.append("q", this.search.trim());
      params.append("page_size", String(this.pageSize));
      params.append("sort", this.defaultSort());
      const cursor = this.pager.cursor();
      if (cursor) params.append("cursor", cursor);
      const qs = params.toString();
      return qs ? `?${qs}` : "";
    },
    async fetchHolds({ reset = false } = {}) {
      if (reset) {
        this.pager.reset();
      }
      const token = ++this.requestToken;
      this.loading = true;
      this.now = Date.now();

      const response = await this.$store.dispatch("holds/fetchHolds", this.buildQuery());

      // Drop stale responses — a newer request has superseded this one.
      if (token !== this.requestToken) return;
      this.loading = false;

      if (!response) {
        this.errorBanner = "Could not reach the server. Please retry.";
        return;
      }
      if (response.status === 403) {
        this.forbidden = true;
        return;
      }
      if (response.status >= 200 && response.status < 300 && response.data) {
        this.forbidden = false;
        this.errorBanner = "";
        this.items = response.data.items || [];
        this.nextCursor = response.data.next_cursor || null;
        return;
      }
      // Keep last-good rows; surface a dismissible banner with Retry.
      const msg = (response.data && response.data.message) || "Failed to load holds.";
      this.errorBanner = msg;
    },

    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.fetchHolds({ reset: true });
      }, 300);
    },
    onFilterChange() {
      // Vue updates v-model before this fires; reset cursor for the new view.
      this.$nextTick(() => this.fetchHolds({ reset: true }));
    },
    refresh() {
      this.fetchHolds();
    },
    clearFilters() {
      this.search = "";
      this.statusFilter = "PENDING";
      this.fetchHolds({ reset: true });
    },
    nextPage() {
      this.pager.next(this.nextCursor);
      this.fetchHolds();
    },
    prevPage() {
      this.pager.prev();
      this.fetchHolds();
    },

    // ── Row rendering helpers ────────────────────────────────────────────
    statusBadgeClass(status) {
      return STATUS_BADGES[status] || "badge-light";
    },
    isExpiredRow(item) {
      return item.status === "PENDING" && isPastExpiry(item.expires_at, this.now);
    },
    rowAction(item) {
      return getRowAction(
        item,
        { confirm: this.hasPermission("hold.confirm"), reverse: this.hasPermission("hold.reverse") },
        this.now,
      );
    },
    reasonPrimary(item) {
      return formatReason(item.reason).primary;
    },
    reasonHasDetail(item) {
      return formatReason(item.reason).fullJson !== "";
    },
    reasonTooltip(item) {
      const json = formatReason(item.reason).fullJson;
      const escaped = json
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return `<pre class="text-left mb-0">${escaped}</pre>`;
    },

    // ── Action flow ──────────────────────────────────────────────────────
    openConfirm(hold) {
      this.confirmModal = { show: true, hold };
    },
    openReverse(hold) {
      this.reverseModal = { show: true, hold };
    },
    async doConfirm() {
      const hold = this.confirmModal.hold;
      this.confirmModal.show = false;
      await this.runAction("confirm", hold);
    },
    async doReverse() {
      const hold = this.reverseModal.hold;
      this.reverseModal.show = false;
      await this.runAction("reverse", hold);
    },
    async runAction(action, hold, idempotencyKey = null) {
      const dispatchName = action === "confirm" ? "holds/confirmHold" : "holds/reverseHold";
      const payload = { holdId: hold.hold_id };
      if (idempotencyKey) payload.idempotency_key = idempotencyKey;

      this.resultModal.loading = true;
      const response = await this.$store.dispatch(dispatchName, payload);
      this.resultModal.loading = false;

      const ok = response && response.status >= 200 && response.status < 300;
      if (ok) {
        const data = response.data || {};
        this.resultModal = {
          show: true,
          action,
          success: true,
          result: data.result || {},
          error: null,
          hold,
          loading: false,
          // Preserve for a possible later Retry chain (no-op on success).
          idempotencyKey: data.idempotency_key || idempotencyKey,
        };
        this.applyOptimisticUpdate(hold, action, data.result || {});
      } else {
        // Preserve the idempotency key the backend generated so Retry is safe.
        const data = (response && response.data) || {};
        this.resultModal = {
          show: true,
          action,
          success: false,
          result: {},
          error: { status: response && response.status, data },
          hold,
          loading: false,
          idempotencyKey: data.idempotency_key || idempotencyKey,
        };
      }
    },
    retryAction() {
      this.runAction(this.resultModal.action, this.resultModal.hold, this.resultModal.idempotencyKey);
    },
    applyOptimisticUpdate(hold, action, result) {
      const newStatus = action === "confirm" ? "CONFIRMED" : "REVERSED";
      this.items = this.items.map((it) => {
        if (it.hold_id !== hold.hold_id) return it;
        return {
          ...it,
          status: result.status || newStatus,
          confirmed_at: action === "confirm" ? result.confirmed_at || it.confirmed_at : it.confirmed_at,
          reversed_at: action === "reverse" ? result.reversed_at || it.reversed_at : it.reversed_at,
        };
      });
    },
    closeResult() {
      this.resultModal.show = false;
      // Background refetch picks up other operators' concurrent changes.
      this.fetchHolds();
    },

    // ── Clipboard ────────────────────────────────────────────────────────
    copy(value, label) {
      if (!value) return;
      const done = () => {
        if (this.$toast) this.$toast.open({ message: `${label} copied`, type: "success", duration: 2000, position: "top-right" });
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(() => {});
      } else {
        const el = document.createElement("textarea");
        el.value = value;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        done();
      }
    },
  },
};
</script>
